import { notification } from 'antd';
import { uniq } from 'lodash-es';
import { useCallback } from 'react';
import { errorObjectToString } from 'src/utils/common';
import { getStore, getUser, updateStore, updateUser } from 'src/utils/firebase/firebase-short-cut';
import { AuditLogger } from 'src/utils/audit-logger';
import useSWR from 'swr';

const auditLogger = AuditLogger.getInstance();

const getUserWithStore = async (request: [string, string]) => {
  const [userId] = request;
  const user = await getUser(userId);
  if (!user) throw new Error('User not found');

  if (user?.storeId) {
    const store = await getStore(user.storeId);
    return { ...user, store: store ?? null };
  } else {
    return { ...user, store: null };
  }
};

const useUserWithStore = (userId?: string) => {
  const {
    data: userWithStore,
    error,
    isValidating: userLoading,
    mutate,
  } = useSWR(userId ? [userId, 'user-with-store'] : null, getUserWithStore, {
    revalidateOnReconnect: false,
    revalidateOnFocus: false,
  });

  const mutateUser = useCallback(
    async (field: string, value: any) => {
      if (!userId) {
        console.error('userId is not defined');
        return;
      }

      if (userWithStore === undefined) {
        console.error('userWithStore is not defined');
        return;
      }

      try {
        if (field === 'favoriteProducts') {
          const beforeIds = userWithStore.favoriteProducts ?? [];
          const favoriteProducts = uniq(value as string[]);
          mutate(
            {
              ...userWithStore,
              favoriteProducts,
            },
            false,
          );
          await updateUser(userId, {
            favoriteProducts,
          });
          mutate();
          const userLabel = userWithStore.email ?? userWithStore.userName ?? userId;
          auditLogger.log({
            action: 'UPDATE',
            entity: 'user',
            entityId: userId,
            summary: `사용자 상세 - 즐겨찾기 수정 (${userLabel})`,
            changes: [{ field: 'favoriteProducts', before: beforeIds, after: favoriteProducts }],
          });
          notification.success({
            message: '즐겨찾기 수정 성공',
            description: `${beforeIds.length}개 → ${favoriteProducts.length}개`,
          });
        } else {
          if (field === 'status' && value === 'deleted') {
            notification.info({
              message: '사용자 삭제는 개발자에게 요청해주세요.',
              description: '사용자 상태를 변경할 수 없습니다.',
            });
            auditLogger.log({
              action: 'STATUS_CHANGE',
              entity: 'user',
              entityId: userId,
              summary: '사용자 관리 - 사용자 삭제 요청',
              level: 'error',
            });
            return;
          }
          mutate({
            ...userWithStore,
            [field]: value,
          });
          await updateUser(userId, {
            [field]: value,
          });
          mutate();
          notification.success({
            message: '사용자 상세 정보 수정 성공',
            description: `${field}를 ${value}로 수정하였습니다.`,
          });
        }
      } catch (error: any) {
        notification.error({
          message: '사용자 상세 정보 수정 실패',
          description: error.message,
        });
      }
    },
    [mutate, userId, userWithStore],
  );

  const mutateStore = useCallback(
    async (field: string, value: any) => {
      if (!userWithStore || !userWithStore?.storeId || !userWithStore?.store) {
        console.error('storeId is not defined');
        return;
      }

      try {
        if (field === 'unhiddenProducts') {
          const unhiddenProducts = uniq((value as string).split(',').map((id) => id.trim()));
          mutate(
            {
              ...userWithStore,
              store: { ...userWithStore.store, unhiddenProducts },
            },
            false,
          );
          await updateStore(
            userWithStore.storeId,
            {
              unhiddenProducts,
            },
            { audit: { suppress: true } },
          );
          mutate();
        } else {
          mutate(
            {
              ...userWithStore,
              store: { ...userWithStore.store, [field]: value },
            },
            false,
          );
          await updateStore(
            userWithStore.storeId,
            {
              [field]: value,
            },
            { audit: { suppress: true } },
          );
          mutate();
        }
        notification.success({
          message: '업소 상세 정보 수정 성공',
          description: `${field}를 ${value}로 수정하였습니다.`,
        });
        auditLogger.log({
          action: 'UPDATE',
          entity: 'store',
          entityId: userWithStore.storeId,
          summary: `업소 상세 정보 수정 - ${field}`,
          changes: [{ field, before: undefined, after: value }],
          metadata: { userId: userWithStore._id },
        });
      } catch (error) {
        const description = errorObjectToString(error);
        notification.error({
          message: '업소 상세 정보 수정 실패',
          description,
        });
        auditLogger.log({
          action: 'UPDATE',
          entity: 'store',
          entityId: userWithStore.storeId,
          summary: `업소 상세 정보 수정 실패: ${description}`,
          level: 'error',
        });
      }
    },
    [mutate, userWithStore],
  );

  return { userWithStore, error, userLoading, mutateUser, mutateStore };
};

export default useUserWithStore;
